import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  effect,
  inject,
  input,
  InputSignal,
  OnInit,
  ViewChild,
  Signal,
  computed,
  ContentChild,
  ContentChildren,
  QueryList,
  AfterViewChecked,
} from '@angular/core';
import { NgClass, NgStyle } from '@angular/common';
import { ButtonComponent } from '../../feedback/button/button.component';
import { CorporateDesignService } from '../../../services/corporate-design/corporate-design.service';

@Component({
  selector: ' aw-section',
  imports: [NgClass, ButtonComponent, NgStyle],
  templateUrl: './section.component.html',
})
export class SectionComponent implements OnInit, AfterViewChecked {
  public readonly isManualDarkMode: InputSignal<boolean> =
    input<boolean>(false);
  public readonly darkModeColor: InputSignal<string> = input<string>('#000000');
  public readonly darkModeBorderColor: InputSignal<string> =
    input<string>('#FFFFFF');
  public readonly isDisabled: InputSignal<boolean> = input<boolean>(false);
  public readonly isCollapsible: InputSignal<boolean> = input<boolean>(false);
  public readonly reverseHeader: InputSignal<boolean> = input<boolean>(false);
  public readonly isInitiallyCollapsed: InputSignal<boolean> =
    input<boolean>(false);
  public readonly showDividerWhenCollapsed: InputSignal<boolean> =
    input<boolean>(false);
  public readonly isStickyHeader: InputSignal<boolean> = input<boolean>(false);
  public readonly withoutContentPadding: InputSignal<boolean> =
    input<boolean>(false);
  public readonly testIdAffix: InputSignal<string> = input.required<string>();
  public readonly id: InputSignal<string> = input<string>('');
  public readonly forceCollapse: InputSignal<boolean | null | undefined> =
    input<boolean | null | undefined>(false);
  public readonly triggerExpandByHash: InputSignal<string | null | undefined> =
    input<string | null | undefined>();
  private readonly cdRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  protected readonly corporateDesignService: CorporateDesignService = inject(
    CorporateDesignService
  );

  protected readonly isDarkMode: Signal<boolean> = computed(() => {
    return this.isManualDarkMode()
      ? this.isManualDarkMode()
      : this.corporateDesignService.isDarkModeActive();
  });

  public hasMultipleChildren: boolean = false;
  public isCollapsed: boolean = false;

  constructor() {
    effect((): void => {
      const forceCollapse: boolean | null | undefined = this.forceCollapse();
      if (
        forceCollapse !== null &&
        forceCollapse !== undefined &&
        forceCollapse !== this.isCollapsed
      ) {
        this.isCollapsed = forceCollapse;
        this.cdRef.markForCheck();
      }
    });
  }

  @ViewChild('section')
  protected readonly sectionRef!: ElementRef;

  @ContentChild('headlineText', { descendants: true })
  private readonly headlineTextRef!: ElementRef;
  @ContentChildren('subSection', { descendants: true })
  public readonly subSectionsRef!: QueryList<SectionComponent>;

  get headline(): string {
    if (!this.headlineTextRef?.nativeElement?.innerHTML) {
      console.warn(
        `Cannot find #headlineText for section-headline for section ${this.id()}`
      );
    }
    return this.headlineTextRef?.nativeElement?.innerHTML || '';
  }

  get subSections(): SectionComponent[] {
    this.hasMultipleChildren =
      this.sectionRef.nativeElement.children.length > 1;
    return this.subSectionsRef.map((c: SectionComponent) => c);
  }

  ngOnInit() {
    this.isCollapsed = this.forceCollapse() ?? this.isInitiallyCollapsed();
  }

  ngAfterViewChecked() {
    this.hasMultipleChildren =
      this.sectionRef.nativeElement.children.length > 1;
    this.cdRef.detectChanges();
  }

  toggleCollapse(): void {
    if (this.isCollapsible()) {
      this.isCollapsed = !this.isCollapsed;
      if (this.isCollapsed) {
        history.replaceState(null, '', location.pathname + location.search);
      }
    }
    this.cdRef.detectChanges();
  }

  getBoundingClientRect() {
    return this.sectionRef?.nativeElement.getBoundingClientRect();
  }
}
